import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
} from "@angular/common/http";
import { finalize, tap } from "rxjs/operators";
import { Observable } from "rxjs";
import { AdminService } from "./admin.service";
import { DataSharingService } from "./data.share.service";
import { Router } from "@angular/router";

@Injectable({ providedIn: "root" })
export class InterceptorService implements HttpInterceptor {
  private activeRequests = 0;

  // ⬇️ Configura tu mínimo de visibilidad del loader (3s)
  private readonly MIN_VISIBLE_MS = 2000;

  // Estado interno para controlar el ciclo de “mostrar/ocultar”
  private firstShownAt = 0; // timestamp cuando se mostró el loader
  private hideTimer: any = null; // timer pendiente para ocultar

  private language: 1 | 2 | 3;

  constructor(
    private admin: AdminService,
    private localStorage: DataSharingService,
    private router: Router
  ) {
    const lang = this.localStorage.getItem("language");
    this.language = lang === "2" ? 2 : lang === "3" ? 3 : 1;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isVersionCheck = req.url.includes("/assets/version.json");
    const skipLoaderVersionamiento = isVersionCheck || req.headers.get("x-no-loader") === "true";
    // Token
    const tokenStr = this.localStorage.getItem("accessInfo");
    const token = tokenStr ? JSON.parse(tokenStr) : null;
    if (token?.accessToken && !req.url.includes(".pdf") && !isVersionCheck) {
      req = req.clone({ setHeaders: { authorization: token.accessToken } });
    }
    if (skipLoaderVersionamiento) {
      const clean = req.clone({ headers: req.headers.delete("x-no-loader") });
      return next.handle(clean);
    }
    // Saltar loader en preflight u opt-in por header
    const skipLoader =
      req.method === "OPTIONS" || req.headers.get("X-Skip-Loader") === "true";

    if (!skipLoader) {
      // Si es el primer request del ciclo, mostramos loader y marcamos tiempo
      if (this.activeRequests === 0) {
        if (this.hideTimer) {
          clearTimeout(this.hideTimer);
          this.hideTimer = null;
        }
        this.firstShownAt = Date.now();
        this.admin.loader(true); // prender loader
      }
      this.activeRequests++;
    }

    return next.handle(req).pipe(
      tap({
        error: (err) => this.handleHttpError(err),
      }),
      finalize(() => {
        if (!skipLoader) {
          this.activeRequests = Math.max(0, this.activeRequests - 1);

          // Si ya no quedan requests, espera a cumplir el mínimo antes de apagar
          if (this.activeRequests === 0) {
            const elapsed = Date.now() - this.firstShownAt;
            const remaining = Math.max(0, this.MIN_VISIBLE_MS - elapsed);

            if (this.hideTimer) {
              clearTimeout(this.hideTimer);
            }
            this.hideTimer = setTimeout(() => {
              // doble-check: que no haya empezado otro request
              if (this.activeRequests === 0) {
                this.admin.loader(false); // apagar loader
                this.firstShownAt = 0;
              }
              this.hideTimer = null;
            }, remaining);
          }
        }
      })
    );
  }

  private handleHttpError(err: any) {
    if (err instanceof HttpErrorResponse) {
      let language: any = localStorage.getItem("language");

      let languageSet = language == 1 ? 1 : language == 2 ? 2 : 3;

      if (
        err.error.statusCode == 401 &&
        ((err.error.message == "You have been logged out" &&
          this.language == 2) ||
          (err.error.message == "Has sido desconectado" &&
            this.language == 1) ||
          (err.error.message == "Você foi desconectado" &&
            this.language == 3) ||
          (err.error.message ==
            "Your are unauthorized. Please login or create account to get access." &&
            this.language == 2) ||
          (err.error.message ==
            "No estás autorizado. Inicie sesión o cree una cuenta para obtener acceso." &&
            this.language == 1) ||
          (err.error.message ==
            "Você não está autorizado. Faça login ou crie uma conta para obter acesso." &&
            this.language == 3) ||
          (err.error.message ==
            "Your account has been blocked by the Admin. Please contact support@support.com." &&
            this.language == 2) ||
          (err.error.message ==
            "El administrador ha bloqueado su cuenta. Por favor póngase en contacto con support@support.com." &&
            this.language == 1) ||
          (err.error.message ==
            "Sua conta foi bloqueada pelo administrador. Entre em contato com support@support.com." &&
            this.language == 3) ||
          (err.error.message == "The token is not valid or User not Found!" &&
            (this.language == 2 || this.language == 1)) ||
          (err.error.message ==
            "El token no es válido o el usuario no fue encontrado!" &&
            this.language == 1) ||
          (err.error.message ==
            "O token não é válido ou Usuário não encontrado!" &&
            this.language == 3))
      ) {
        this.localStorage.clear();
        // this.admin.logOut();
        this.router.navigate(["/login"]);
      } else if (err.error.statusCode == 403) {
        this.admin.errorAlert(err.error.message, 403, languageSet);
      } else if (err.error.statusCode == 408) {
        this.admin.errorAlert(err.error.message, 408, languageSet);
      } else if (err.error.statusCode == 401 || err.error.statusCode == 405) {
        if (this.checkMessageErrorAccountExist(err.error.message)) {
          this.admin.errorAlert(err.error.message, 401, languageSet);
        }
      } else if (
        err.error.statusCode == 400 &&
        ((err.error.message == "This url has been expired." &&
          this.language == 2) ||
          (err.error.message == "Esta URL ha caducado." &&
            this.language == 1) ||
          (err.error.message == "Este URL expirou." && this.language == 3))
      ) {
        let token: any = this.localStorage.getItem("accessInfo")
          ? JSON.parse(this.localStorage.getItem("accessInfo") || "")
          : "";

        if (token.accessToken) {
          this.admin.errorAlertmsg(
            this.language == 2
              ? "Please logout the main account than login with another"
              : this.language == 1
              ? "Por favor cierre sesión en la cuenta principal y luego inicie sesión con otra"
              : "Saia da conta principal e faça login com outrar",

            400
          );
        } else {
          this.admin.errorAlertmsg(
            this.language == 2
              ? "This url has been expired."
              : this.language == 1
              ? "Esta URL ha caducado."
              : "Este URL expirou.",

            400
          );
          this.router.navigate(["/login"]);
        }
      } else if (err.error.statusCode == 400) {
        if (
          err.error.message == "Payment already initiated." ||
          err.error.message == "Booking not found." ||
          err.error.message == "Something went wrong." ||
          err.error.message ===
            "Esta dirección de correo electrónico no está asociada con la aplicación Buki." ||
          err.error.message ===
            "This email address is not associated with buki app." ||
          err.error.message ===
            "¡Ups! El código de verificación que ingresaste es incorrecto. Inténtalo de nuevo."
        ) {
        } else {
          if (!this.hasKey(err.error.message, "title")) {
            this.admin.errorAlert(err.error.message, 400, languageSet);
          }
        }
      } else {
      }
    }
  }

  hasKey(obj: any, key: string): boolean {
    return obj && Object.prototype.hasOwnProperty.call(obj, key);
  }

  checkMessageErrorAccountExist(message: string) {
    const listText = [
      "Este correo electrónico ya está asociado con una cuenta registrada por número de teléfono. Inicie sesión con su número de teléfono.",
      "Este correo electrónico ya está asociado con una cuenta registrada. Inicie sesión con su correo electrónico.",
      "Este correo electrónico ya está asociado con una cuenta registrada por número de teléfono. Inicie sesión con su número de teléfono.",
      "Este correo electrónico ya está asociado con una cuenta registrada por Google. Inicie sesión con su cuenta de Google.",
      "Este número de teléfono ya está asociado con una cuenta registrada por correo electrónico. Inicie sesión con su correo electrónico",
      "Este número de teléfono ya está asociado con una cuenta registrada por número de teléfono. Inicie sesión con su número de teléfono.",
      "Este número de teléfono ya está asociado con una cuenta registrada por correo electrónico. Inicie sesión con su correo electrónico.",
      "Este número de teléfono ya está asociado con una cuenta registrada por Google. Inicie sesión con su cuenta de Google.",
      "This email address is already associated with an account registered by phone number. Please log in with your phone number.",
      "This email address is already associated with a registered account. Please log in with your email address.",
      "This email address is already associated with an account registered by phone number. Please log in with your phone number.",
      "This email address is already associated with a Google account. Please sign in with your Google account.",
      "This phone number is already associated with an account registered by email. Please log in with your email address.",
      "This phone number is already associated with an account registered by phone number. Please log in with your phone number.",
      "This phone number is already associated with an account registered by email. Please log in with your email address.",
      "This phone number is already associated with a Google account. Please sign in with your Google account.",
      "Este endereço de e-mail já está associado a uma conta registada por número de telefone. Inicie sessão com o seu número de telefone.",
      "Este endereço de e-mail já está associado a uma conta registada. Inicie sessão com o seu endereço de e-mail.",
      "Este endereço de e-mail já está associado a uma conta registada por número de telefone. Inicie sessão com o seu número de telefone.",
      "Este endereço de e-mail já está associado a uma conta Google. Inicie sessão com a sua conta Google.",
      "Este número de telefone já está associado a uma conta registada por e-mail. Inicie sessão com o seu endereço de e-mail.",
      "Este número de telefone já está associado a uma conta registada por número de telefone. Inicie sessão com o seu número de telefone.",
      "Este número de telefone já está associado a uma conta registada por e-mail. Inicie sessão com o seu endereço de e-mail.",
      "Este número de telefone já está associado a uma conta Google. Inicie sessão com a sua conta Google.",
    ];

    if (!listText.includes(message)) {
      return true;
    } else {
      return false;
    }
  }
}
